import { forwardRef, useEffect, useRef } from "react";
import type { ShowcaseCapability } from "../../types.ts";
import { SlideEyebrow, SlideHeading, StatusPill } from "../common.tsx";

interface Props {
  index: number;
  capabilities: ShowcaseCapability[];
}

export const AllCapsSlide = forwardRef<HTMLElement, Props>(function AllCapsSlide(
  { index, capabilities },
  ref
) {
  const featured = capabilities.filter((c) => c.image);
  const listRef = useRef<HTMLDivElement>(null);

  /* Animate each row in when it scrolls into view */
  useEffect(() => {
    const rows = listRef.current?.querySelectorAll<HTMLElement>(".dz-feat-row");
    if (!rows?.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const el = e.target as HTMLElement;
            el.classList.add("dz-feat-row--visible");
            io.unobserve(el);
          }
        });
      },
      {
        threshold: 0.12,
        /* negative bottom margin so trigger fires as card enters from below */
        rootMargin: "0px 0px -60px 0px",
      }
    );

    rows.forEach((r) => io.observe(r));
    return () => io.disconnect();
  }, [featured.length]);

  return (
    <section ref={ref} className="dz-slide dz-slide--allcaps">
      <div className="dz-container">
        <SlideEyebrow index={index} label="Full Capability Surface" />
        <SlideHeading plain="The complete "><em>real</em> picture.</SlideHeading>
        <p className="dz-lede">
          Every capability below is implemented in the codebase. No roadmap items live here.
        </p>

        <div className="dz-feat-list" ref={listRef}>
          {featured.map((cap, i) => (
            <div
              key={i}
              className={`dz-feat-row${i % 2 === 1 ? " dz-feat-row--flip" : ""}`}
              style={{ "--dz-feat-delay": `${i * 0.08}s` } as React.CSSProperties}
            >
              <div className="dz-feat-img-wrap">
                <img
                  src={cap.image}
                  alt={cap.title}
                  className="dz-feat-img"
                  draggable={false}
                />
              </div>
              <div className="dz-feat-body">
                {cap.status && (
                  <div className="dz-feat-pill">
                    <StatusPill status={cap.status} />
                  </div>
                )}
                <h3 className="dz-feat-h">{cap.title}</h3>
                <p className="dz-feat-p">{cap.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
});

